const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface DayRange {
  /** Minutes from midnight. */
  start: number;
  end: number;
}

/** Weekly availability, keyed by day of week (0 = Sunday). Empty/absent = unavailable. */
export type WeeklyHoursValue = Record<number, DayRange[]>;

export interface WeeklyHoursProps {
  value: WeeklyHoursValue;
  onChange: (value: WeeklyHoursValue) => void;
}

const toHHMM = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const toMin = (s: string) => {
  const [h, mm] = s.split(':').map(Number);
  return (h || 0) * 60 + (mm || 0);
};

/** Weekly hours editor — per-day on/off with one or more time ranges. */
export function WeeklyHours({ value, onChange }: WeeklyHoursProps) {
  const setDay = (day: number, ranges: DayRange[]) => onChange({ ...value, [day]: ranges });

  return (
    <div>
      {DAYS.map((name, day) => {
        const ranges = value[day] ?? [];
        const enabled = ranges.length > 0;
        return (
          <div key={day} className="d-flex align-items-start py-2 border-top">
            <label className="form-check form-switch me-3 mb-0" style={{ minWidth: 150 }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={enabled}
                onChange={(e) => setDay(day, e.target.checked ? [{ start: 540, end: 1020 }] : [])}
              />
              <span className="form-check-label fw-semibold">{name}</span>
            </label>

            <div className="flex-grow-1">
              {!enabled && <span className="text-muted">Unavailable</span>}
              {ranges.map((r, i) => (
                <div key={i} className="d-flex align-items-center gap-2 mb-2">
                  <input
                    type="time"
                    className="form-control"
                    style={{ width: 140 }}
                    value={toHHMM(r.start)}
                    onChange={(e) =>
                      setDay(day, ranges.map((x, j) => (j === i ? { ...x, start: toMin(e.target.value) } : x)))
                    }
                  />
                  <span>–</span>
                  <input
                    type="time"
                    className="form-control"
                    style={{ width: 140 }}
                    value={toHHMM(r.end)}
                    onChange={(e) =>
                      setDay(day, ranges.map((x, j) => (j === i ? { ...x, end: toMin(e.target.value) } : x)))
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-light btn-icon"
                    aria-label="Remove"
                    onClick={() => setDay(day, ranges.filter((_, j) => j !== i))}
                  >
                    <i className="ph-x" />
                  </button>
                  {i === ranges.length - 1 && (
                    <button
                      type="button"
                      className="btn btn-light btn-icon"
                      aria-label="Add range"
                      onClick={() => setDay(day, [...ranges, { start: 540, end: 1020 }])}
                    >
                      <i className="ph-plus" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
